<template>
  <div class="card scan-folheto-card">
    <div class="card-header pb-0">
      <h6 class="mb-0">Scan de Folheto</h6>
      <p class="text-sm text-secondary mb-0">Identifique produtos e preços automaticamente com IA</p>
    </div>

    <div class="card-body">
      <!-- Upload de imagem -->
      <label class="form-label-custom">
        <i class="fas fa-image me-1"></i>Imagem do Folheto
      </label>
      <div
        class="upload-zone"
        :class="{ 'drag-over': isDragging, 'has-file': selectedFile }"
        @click="triggerFileInput"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <template v-if="!selectedFile">
          <div class="upload-placeholder">
            <div class="upload-icon-wrapper">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <p class="mb-1 text-sm font-weight-bold">Arraste ou clique para selecionar</p>
            <p class="mb-0 text-xs text-secondary">PNG, JPG, JPEG, WEBP</p>
          </div>
        </template>
        <template v-else>
          <div class="upload-preview-row">
            <img :src="previewUrl" alt="Preview do folheto" class="preview-thumb" />
            <div class="preview-meta">
              <i class="fas fa-check-circle text-success me-1"></i>
              <span class="text-sm text-truncate">{{ selectedFile.name }}</span>
            </div>
            <button class="btn-remove-file" @click.stop="clearFile" title="Remover imagem">
              <i class="fas fa-times-circle"></i>
            </button>
          </div>
        </template>
      </div>
      <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="handleFileChange" />

      <!-- Erro -->
      <div v-if="scanError" class="alert alert-danger py-2 mt-3 mb-0">
        <i class="fas fa-exclamation-circle me-2"></i>{{ scanError }}
      </div>

      <!-- Botão escanear -->
      <div class="d-flex justify-content-end mt-3">
        <button
          class="btn btn-custom-gradient btn-scan"
          :disabled="!selectedFile || scanning"
          @click="scanFolheto"
        >
          <span v-if="scanning" class="d-flex align-items-center gap-2">
            <span class="spinner-border spinner-border-sm"></span>
            Analisando imagem...
          </span>
          <span v-else class="d-flex align-items-center gap-2">
            <i class="fas fa-search"></i>
            Escanear Folheto
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest, handleApiResponse, API_ENDPOINTS } from '@/config/api.js';
import { showSuccessAlert, showErrorAlert } from '@/utils/swal.js';

export default {
  name: 'ScanFolhetoTable',

  data() {
    return {
      selectedFile: null,
      previewUrl: null,
      isDragging: false,
      scanning: false,
      scanError: null,
    };
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) this.setFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.setFile(file);
    },

    setFile(file) {
      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
      this.scanError = null;
    },

    clearFile() {
      this.selectedFile = null;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },

    async scanFolheto() {
      if (!this.selectedFile) return;

      this.scanning = true;
      this.scanError = null;

      try {
        const formData = new FormData();
        formData.append('image', this.selectedFile);

        const response = await apiRequest(API_ENDPOINTS.PRODUCT_SCAN, {
          method: 'POST',
          body: formData,
        });
        await handleApiResponse(response);

        await showSuccessAlert('Scan concluído!', 'Os produtos do folheto foram inseridos no banco de dados com sucesso.');
        this.clearFile();
      } catch (err) {
        this.scanError = err.message;
        showErrorAlert('Erro no scan', err.message);
      } finally {
        this.scanning = false;
      }
    },
  },
};
</script>

<style scoped>
.scan-folheto-card {
  border: none;
  border-radius: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.form-label-custom {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 6px;
  display: block;
}

.upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 0.75rem;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.upload-zone:hover,
.upload-zone.drag-over {
  border-color: #17c1e8;
  background: rgba(23, 193, 232, 0.04);
}

.upload-zone.has-file {
  border-color: #20c997;
  border-style: solid;
  background: rgba(32, 201, 151, 0.04);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(310deg, rgba(23,193,232,0.12), rgba(32,201,151,0.12));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 24px;
  color: #17c1e8;
}

.upload-preview-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0 8px;
}

.preview-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #20c997;
  flex-shrink: 0;
}

.preview-meta {
  flex: 1;
  text-align: left;
  overflow: hidden;
}

.btn-remove-file {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-remove-file:hover {
  opacity: 1;
}

.btn-scan {
  padding: 0.6rem 2rem;
  border-radius: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.btn-custom-gradient {
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-custom-gradient:hover:not(:disabled) {
  background: linear-gradient(310deg, #15a8d1 0%, #1bb88a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(23, 193, 232, 0.35);
}

.btn-custom-gradient:active {
  transform: translateY(0);
}

.btn-custom-gradient:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
</style>
